import axios from "axios";

// Use environment variable directly - NO additional prefixes
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  "https://api.nephrotrax.com";

console.log("🌍 API Base URL:", API_BASE_URL);
console.log(
  "🌍 Environment:",
  process.env.REACT_APP_ENVIRONMENT || "development"
);

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Store Auth0 token getter function
let getAccessTokenFunction = null;

/**
 * Initialize API service with Auth0 token getter
 */
export const initializeApiWithAuth0 = (getAccessTokenSilently) => {
  getAccessTokenFunction = getAccessTokenSilently;
  console.log("✅ API service initialized with Auth0");
  console.log("🔧 Audience:", process.env.REACT_APP_AUTH0_AUDIENCE);
};

// Request interceptor - automatically add Auth0 token
api.interceptors.request.use(
  async (config) => {
    console.log("📤 API Request:", config.method?.toUpperCase(), config.url);
    console.log("🔍 Full URL:", `${config.baseURL}${config.url}`);
    console.log("🔍 Has token getter:", !!getAccessTokenFunction);

    // Skip auth for public endpoints
    const publicEndpoints = ["/v1/auth/signup", "/test/config", "/v1/travel/tiers"];
    const isPublicEndpoint = publicEndpoints.some((endpoint) =>
      config.url?.includes(endpoint)
    );

    if (isPublicEndpoint) {
      console.log("🌐 Public endpoint - skipping auth token");
      return config;
    }

    if (getAccessTokenFunction) {
      try {
        console.log("🔄 Fetching Auth0 token...");
        console.log("🔧 Audience:", process.env.REACT_APP_AUTH0_AUDIENCE);

        let token;

        try {
          token = await getAccessTokenFunction({
            authorizationParams: {
              audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            },
          });
        } catch (error) {
          console.warn("⚠️ Silent token failed:", error.message);
          if (
            error.error === "consent_required" ||
            error.error === "login_required"
          ) {
            console.error(
              "🔐 User consent required - please log out and log in again"
            );
          }
          throw error;
        }

        console.log("✅ Token obtained from Auth0");
        console.log("🔑 Token preview:", token.substring(0, 30) + "...");

        try {
          const payload = JSON.parse(atob(token.split(".")[1]));
          console.log("📋 Token audience:", payload.aud);
          console.log("📋 Token scope:", payload.scope);
          console.log("📋 Token issued at:", new Date(payload.iat * 1000).toLocaleString());
          console.log("📋 Token expires at:", new Date(payload.exp * 1000).toLocaleString());
          const permissions =
            payload.permissions ||
            payload["https://api.nephrotrax.com/permissions"] ||
            [];
          console.log("📋 Token permissions:", permissions.length > 0 ? permissions : "NONE");
        } catch (decodeError) {
          console.error("❌ Failed to decode token:", decodeError);
        }

        config.headers["Authorization"] = `Bearer ${token}`;
        console.log("✅ Authorization header added");
      } catch (error) {
        console.error("❌❌❌ ERROR GETTING TOKEN ❌❌❌");
        console.error("Error:", error.message);
        console.error("Error details:", error);
      }
    } else {
      console.warn("⚠️ Auth0 token getter not initialized - request will be unauthenticated");
    }

    return config;
  },
  (error) => {
    console.error("❌ API Request Error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    console.log("✅ API Response:", response.status, response.config.url);
    return response;
  },
  (error) => {
    if (error.response) {
      console.error("❌ API Error:", error.response.status, error.config?.url);
      console.error("📋 Error data:", error.response.data);
      if (error.response.status === 401) {
        console.error("🔒 Unauthorized - Token may be invalid or missing permissions");
        console.error("💡 Try logging out and logging back in");
      } else if (error.response.status === 403) {
        console.error("🚫 Forbidden - Insufficient permissions");
        console.error("💡 Contact admin to get required permissions");
      } else if (error.response.status === 404) {
        console.error("🔍 Not Found - Resource doesn't exist");
      } else if (error.response.status === 500) {
        console.error("💥 Server Error - Backend issue");
      }
    } else if (error.request) {
      console.error("🌐 Network Error - No response from server");
      console.error("💡 Is the backend running on", API_BASE_URL, "?");
    } else {
      console.error("❌ Request Setup Error:", error.message);
    }
    return Promise.reject(error);
  }
);



// ==================== API SERVICE METHODS ====================

export const apiService = {
  // ===== SIGNUP (PUBLIC - NO AUTH) =====
  signupUser: async (signupData) => {
    try {
      console.log("📝 Signup request:", signupData.email);
      const response = await api.post("/v1/auth/signup", signupData);
      console.log("✅ Signup successful:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ Signup error:", error.response?.data || error.message);
      throw error;
    }
  },

  // ===== DELETE ACCOUNT =====
  // Permanently deletes the account and all associated data — addresses
  // App Store Guideline 5.1.1(v). Backend cancels any active Stripe
  // subscription and deletes the Auth0 identity as part of this call.
  deleteAccount: async () => {
    try {
      const response = await api.delete("/v1/auth/account");
      console.log("✅ Account deleted");
      return response.data;
    } catch (error) {
      console.error("❌ Account deletion error:", error.response?.data || error.message);
      throw error;
    }
  },

  // ===== ADMIN - PENDING USERS =====
  getPendingUsers: async () => {
    try {
      console.log("📋 Fetching pending users...");
      const response = await api.get("/v1/auth/pending-users");
      console.log("✅ Found pending users:", response.data.length);
      return response.data;
    } catch (error) {
      console.error("❌ Failed to fetch pending users:", error.response?.data || error.message);
      throw error;
    }
  },

  // ===== ADMIN - APPROVE USER =====
  approveUser: async (userId, approvePractice = true) => {
    try {
      console.log("✅ Approving user:", userId, "approvePractice:", approvePractice);
      const response = await api.post(`/v1/auth/approve-user/${userId}`, { approvePractice });
      console.log("✅ User approved:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ Failed to approve user:", error.response?.data || error.message);
      throw error;
    }
  },

  // ===== ADMIN - REJECT USER =====
  rejectUser: async (userId, reason) => {
    try {
      console.log("❌ Rejecting user:", userId);
      const response = await api.post(`/v1/auth/reject-user/${userId}`, { reason });
      console.log("✅ User rejected:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ Failed to reject user:", error.response?.data || error.message);
      throw error;
    }
  },

  // Practice-change requests from already-approved providers — see
  // ProviderAccountController's /me/practice endpoint.
  getPendingPracticeChanges: async () => {
    const response = await api.get("/v1/auth/pending-practice-changes");
    return response.data;
  },
  approvePracticeChange: async (organizationId) => {
    const response = await api.post(`/v1/auth/approve-practice/${organizationId}`);
    return response.data;
  },
  rejectPracticeChange: async (organizationId) => {
    const response = await api.post(`/v1/auth/reject-practice/${organizationId}`);
    return response.data;
  },

  // ===== PATIENTS =====
  getPatientProfile: async () => {
    const response = await api.get("/v1/patients/me");
    return response.data;
  },

  updatePatientProfile: async (data) => {
    const response = await api.put("/v1/patients/me", data);
    return response.data;
  },

  // App helper for tool use
  sendAppHelperMessage: async (message, conversationHistory) => {
    const response = await api.post('/v1/app-helper/chat', { message, conversationHistory });
    return response.data;
},

getEpicAuthorizeUrl: async ({ redirectUri, codeChallenge, state }) => {
    const response = await api.get('/v1/epic/authorize-url', {
      params: { redirectUri, codeChallenge, state },
    });
    return response.data; // { authorizeUrl: string }
  },

  exchangeEpicCode: async ({ code, codeVerifier, redirectUri }) => {
    const response = await api.post('/v1/epic/token-exchange', {
      code, codeVerifier, redirectUri,
    });
    return response.data;
  },

  syncEpicRecords: async () => {
    const response = await api.post('/v1/epic/sync');
    return response.data;
  },

  getEpicConnectionStatus: async () => {
    const response = await api.get('/v1/epic/status');
    return response.data; // { connected: boolean }
  },

  getEpicLabs: async () => {
  const response = await api.get('/v1/epic/labs');
  return response.data;
},
getEpicLabsForPatient: async (patientId) => {
  const response = await api.get(`/v1/epic/labs/${patientId}`);
  return response.data;
},

// Manual lab entry — for providers without an Epic connection, or any
// lab a provider wants to record by hand. Writes into the same table
// Epic sync uses, so entries appear alongside synced ones automatically.
getAllowedManualLabFields: async (patientId) => {
  const response = await api.get(`/v1/patients/${patientId}/labs/allowed-fields`);
  return response.data; // { fieldKey: displayName, ... }
},
addManualLabValue: async (patientId, entry) => {
  const response = await api.post(`/v1/patients/${patientId}/labs`, entry);
  return response.data;
},
deleteManualLabValue: async (patientId, labValueId) => {
  const response = await api.delete(`/v1/patients/${patientId}/labs/${labValueId}`);
  return response.data;
},

// Admin: managing which practices a Provider is affiliated with. Adding
// a provider to a practice does NOT change their active practice — the
// provider self-service switches into it from the header (see
// ProviderAccountController.addProviderToPractice's own comment).
getAllProviderAccounts: async () => {
  const response = await api.get("/v1/provider-account/admin/list");
  return response.data;
},
addProviderToPractice: async (providerAccountId, organizationId) => {
  const response = await api.post(
    `/v1/provider-account/admin/${providerAccountId}/practices`,
    { organizationId }
  );
  return response.data;
},
removeProviderFromPractice: async (providerAccountId, organizationId) => {
  const response = await api.delete(
    `/v1/provider-account/admin/${providerAccountId}/practices/${organizationId}`
  );
  return response.data;
},
blockProvider: async (providerAccountId) => {
  const response = await api.post(`/v1/provider-account/admin/${providerAccountId}/block`);
  return response.data;
},
unblockProvider: async (providerAccountId) => {
  const response = await api.post(`/v1/provider-account/admin/${providerAccountId}/unblock`);
  return response.data;
},

// Admin: full practice (organization) management
getAllPractices: async () => {
  const response = await api.get("/v1/organizations/admin/list");
  return response.data;
},
createPractice: async (practice) => {
  const response = await api.post("/v1/organizations/admin/create", practice);
  return response.data;
},
deletePractice: async (organizationId) => {
  const response = await api.delete(`/v1/organizations/admin/${organizationId}`);
  return response.data;
},
getPendingPatientPracticeRequests: async () => {
  const response = await api.get("/v1/organizations/admin/pending-patient-requests");
  return response.data;
},
approvePatientPracticeRequest: async (organizationId) => {
  const response = await api.post(`/v1/organizations/admin/${organizationId}/approve-patient-request`);
  return response.data;
},
rejectPatientPracticeRequest: async (organizationId) => {
  const response = await api.post(`/v1/organizations/admin/${organizationId}/reject-patient-request`);
  return response.data;
},

// Patient self-service: join an existing verified practice, or propose a
// new one (goes to admin review; see the three functions above).
setMyPracticeAsPatient: async (payload) => {
  const response = await api.post(`/v1/patients/me/practice`, payload);
  return response.data;
},

// Daily check-in reminders (Web Push)
getVapidPublicKey: async () => {
  const response = await api.get(`/v1/patients/push/vapid-public-key`);
  return response.data.publicKey;
},
getMyReminderSettings: async () => {
  const response = await api.get(`/v1/patients/me/reminder-settings`);
  return response.data;
},
updateMyReminderSettings: async (settings) => {
  const response = await api.put(`/v1/patients/me/reminder-settings`, settings);
  return response.data;
},
saveMyPushSubscription: async (subscription) => {
  const response = await api.post(`/v1/patients/me/push-subscription`, subscription);
  return response.data;
},
deleteMyPushSubscription: async (endpoint) => {
  const response = await api.delete(`/v1/patients/me/push-subscription`, { data: { endpoint } });
  return response.data;
},

  getCarePlanAdherence: async (patientId) => {
    const response = await api.get(`/v1/patients/${patientId}/care-plans/adherence`);
    return response.data;
  },
};

export default api;